import { Popover, PopoverBackdrop, PopoverButton, PopoverPanel } from '@headlessui/react';
import classNames from 'classnames';
import Link from 'next/link';
import type React from 'react';
import { useMemo } from 'react';

import { CinemaDto } from '@cineamo/api/models/cinema/CinemaDto.types';
import { CinemaChainDto } from '@cineamo/api/models/cinema-chain/CinemaChainDto.types';

import { withOpacity } from '@cineamo/ui/src/helpers/color-helper';

import { IconDown16 } from '@cineamo/ui/src/icons/ic-down';
import { IconLocation16 } from '@cineamo/ui/src/icons/ic-location';
import { IconUp16 } from '@cineamo/ui/src/icons/ic-up';

import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';

const PartnerLinksDropdown: React.FC<{
    cinemaChain: CinemaChainDto | null;
    cinemasInChain: CinemaDto[];
}> = ({ cinemaChain, cinemasInChain }) => {
    const domainConfiguration = useDomainConfiguration();
    const selectedCinema = domainConfiguration.cinema;
    const partnerLinksDropdownConfiguration = domainConfiguration.websiteConfiguration.partnerLinksDropdown ?? {};

    const cinemasLinks = useMemo(
        () =>
            cinemasInChain
                .map((cinema) => ({
                    ...cinema,
                    domain: cinema._embedded?.cinemaConfiguration?.domain
                }))
                .filter(
                    ({ domain, id }) => domain && !partnerLinksDropdownConfiguration?.excludedCinemaIds?.includes(id)
                ),
        [cinemasInChain, partnerLinksDropdownConfiguration.excludedCinemaIds]
    );

    const additionalLinks = partnerLinksDropdownConfiguration?.additionalLinks ?? [];

    const popoverLinkStyle = {
        color: domainConfiguration.style.onSurfaceColor,
        '--surface-color': domainConfiguration.style.surfaceColor,
        '--hover-bg-color': withOpacity(domainConfiguration.style.primaryColor, 0.2)
    };

    const linkClass =
        'py-12 lg:py-8 px-16 block transition-colors bg-[var(--surface-color)] hover:bg-[var(--hover-bg-color)] text-body-14 leading-[24px]';

    const cinemaChainLabel = partnerLinksDropdownConfiguration?.cinemaChainLabel ?? cinemaChain?.name;

    return (
        <Popover className="relative cursor-pointer whitespace-nowrap lg:ml-[40px]">
            {({ open }) => (
                <>
                    <PopoverButton
                        className={classNames(
                            'w-full max-w-[240px] flex items-center space-x-8 justify-between p-12 bg-[var(--surface-color)]',
                            'rounded-4'
                        )}
                        style={popoverLinkStyle}>
                        <IconLocation16 className="shrink-0" />
                        <span className="hidden lg:block text-body-bold-14 truncate">{selectedCinema.name}</span>
                        {open ? <IconUp16 className="shrink-0" /> : <IconDown16 className="shrink-0" />}
                    </PopoverButton>
                    <PopoverBackdrop className="fixed inset-0 w-full h-full bg-black/80 lg:hidden z-[60]" />
                    <PopoverPanel
                        anchor="top start"
                        className={classNames('rounded-8 [--anchor-gap:10px] z-[60] divide-y divide-white/40')}
                        style={{
                            backgroundColor: domainConfiguration.style.backgroundColor
                        }}>
                        {cinemasLinks.length > 0 && (
                            <div>
                                {cinemasLinks.map((cinema) => (
                                    <Link
                                        style={popoverLinkStyle}
                                        key={cinema.id}
                                        href={`https://${cinema.domain}`}
                                        className={classNames(
                                            linkClass,
                                            selectedCinema.id === cinema.id && 'text-body-bold-14'
                                        )}>
                                        {cinema.name}
                                    </Link>
                                ))}
                            </div>
                        )}
                        {additionalLinks.length > 0 && (
                            <div>
                                {additionalLinks.map((link) => (
                                    <Link
                                        key={link.url}
                                        href={link.url}
                                        style={popoverLinkStyle}
                                        className={classNames(linkClass)}>
                                        {link.label}
                                    </Link>
                                ))}
                            </div>
                        )}
                        {partnerLinksDropdownConfiguration?.cinemaChainUrl && cinemaChainLabel && (
                            <div>
                                <Link
                                    href={partnerLinksDropdownConfiguration.cinemaChainUrl}
                                    style={popoverLinkStyle}
                                    className={classNames(linkClass)}>
                                    {cinemaChainLabel}
                                </Link>
                            </div>
                        )}
                    </PopoverPanel>
                </>
            )}
        </Popover>
    );
};

export default PartnerLinksDropdown;
